/** Request timeout for every POST. See spec §5.6 for rationale. */
const TIMEOUT_MS = 10_000;
const MAX_KEEPALIVE_BODY_BYTES = 60_000;
const MAX_DEBUG_ENCODED_BODY_BYTES = 128_000;

export type TransportConfig = {
  baseUrl: string;
  publishableKey: string;
  debug: boolean;
  sdkVersion?: string;
};

export type TransportResult =
  | { ok: true; status: number; body: unknown }
  | { ok: false; status: number; body: unknown | null };

export function createTransport(cfg: TransportConfig) {
  const base = cfg.baseUrl.replace(/\/+$/, '');

  return {
    async post(path: string, body: unknown): Promise<TransportResult> {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      let debugReport: Record<string, unknown> | null = null;

      try {
        const json = JSON.stringify(body);
        const encoded = await encodeBody(json);
        const debugId = path === '/v1/collect' ? createDebugId() : null;
        const url = buildUrl(base, path, encoded.compressed, cfg.sdkVersion, debugId);
        const keepalive = bodySize(encoded.body) <= MAX_KEEPALIVE_BODY_BYTES;
        if (debugId) {
          debugReport = buildDebugReport({
            debugId,
            path,
            url,
            keepalive,
            sdkVersion: cfg.sdkVersion,
            payload: body,
            encoded,
          });
        }
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            // text/plain avoids the CORS preflight OPTIONS that
            // application/json would trigger, and gives the body an
            // opaque look in devtools. Server has a matching parser
            // that gunzips when ?compression=gzip is set.
            'Content-Type': 'text/plain',
            'X-StaySignals-Key': cfg.publishableKey,
          },
          body: encoded.body,
          keepalive,
          signal: controller.signal,
        });
        clearTimeout(timer);

        const parsed = await parseBody(res);
        if (res.ok) {
          return { ok: true, status: res.status, body: parsed };
        }
        if (cfg.debug) {
          console.error('[StaySignals] non-2xx from', path, { status: res.status, body: parsed });
        }
        reportCollectFailure(base, cfg.publishableKey, debugReport, cfg.debug, {
          type: 'http_error',
          status: res.status,
          responseBody: parsed,
        });
        return { ok: false, status: res.status, body: parsed };
      } catch (err) {
        clearTimeout(timer);
        if (cfg.debug) {
          console.error('[StaySignals] request failed', path, err);
        }
        reportCollectFailure(base, cfg.publishableKey, debugReport, cfg.debug, {
          type: 'fetch_error',
          status: 0,
          errorName: getErrorName(err),
          errorMessage: getErrorMessage(err),
        });
        return { ok: false, status: 0, body: null };
      }
    },
  };
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

type EncodedBody = {
  body: string | ArrayBuffer;
  compressed: boolean;
  compression: CompressionDiagnostics;
};

type CompressionDiagnostics = {
  attempted: boolean;
  compressionStreamAvailable: boolean;
  outcome: 'plain' | 'gzip' | 'invalid_gzip_magic' | 'fallback_plain';
  bodyBytes: number;
  bodyPrefixHex: string;
  errorName?: string;
  errorMessage?: string;
};

/**
 * Gzip via native CompressionStream when available (Chrome 80+, Safari
 * 16.4+, Firefox 113+). Older browsers fall through to plaintext —
 * server accepts both when Content-Type is text/plain.
 */
async function encodeBody(json: string): Promise<EncodedBody> {
  if (typeof CompressionStream === 'undefined') {
    return {
      body: json,
      compressed: false,
      compression: {
        attempted: false,
        compressionStreamAvailable: false,
        outcome: 'plain',
        bodyBytes: bodySize(json),
        bodyPrefixHex: bodyPrefixHex(json),
      },
    };
  }
  try {
    const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
    const buf = await new Response(stream).arrayBuffer();
    return {
      body: buf,
      compressed: true,
      compression: {
        attempted: true,
        compressionStreamAvailable: true,
        outcome: hasGzipMagic(buf) ? 'gzip' : 'invalid_gzip_magic',
        bodyBytes: bodySize(buf),
        bodyPrefixHex: bodyPrefixHex(buf),
      },
    };
  } catch (err) {
    return {
      body: json,
      compressed: false,
      compression: {
        attempted: true,
        compressionStreamAvailable: true,
        outcome: 'fallback_plain',
        bodyBytes: bodySize(json),
        bodyPrefixHex: bodyPrefixHex(json),
        errorName: getErrorName(err),
        errorMessage: getErrorMessage(err),
      },
    };
  }
}

function appendQuery(path: string, q: string): string {
  return (path.includes('?') ? '&' : '?') + q;
}

function buildUrl(
  base: string,
  path: string,
  compressed: boolean,
  sdkVersion: string | undefined,
  debugId: string | null,
): string {
  const query = [
    path === '/v1/collect' && sdkVersion
      ? `sdkVersion=${encodeURIComponent(sdkVersion)}`
      : null,
    path === '/v1/collect' && debugId ? `sdkDebugId=${encodeURIComponent(debugId)}` : null,
    compressed ? 'compression=gzip' : null,
  ].filter((part): part is string => Boolean(part));
  return base + path + (query.length > 0 ? appendQuery(path, query.join('&')) : '');
}

function bodySize(body: EncodedBody['body']): number {
  if (typeof body === 'string') return new TextEncoder().encode(body).byteLength;
  return body.byteLength;
}

function buildDebugReport(input: {
  debugId: string;
  path: string;
  url: string;
  keepalive: boolean;
  sdkVersion: string | undefined;
  payload: unknown;
  encoded: EncodedBody;
}): Record<string, unknown> {
  return {
    debugId: input.debugId,
    sdkVersion: input.sdkVersion ?? null,
    at: new Date().toISOString(),
    path: input.path,
    collect: {
      url: input.url,
      keepalive: input.keepalive,
      bodyBytes: bodySize(input.encoded.body),
      bodyPrefixHex: bodyPrefixHex(input.encoded.body),
      gzipMagic: hasGzipMagic(input.encoded.body),
    },
    compression: compressionSummary(input.encoded),
    encodedBody: encodedBodyCapture(input.encoded.body),
    payload: input.payload,
  };
}

function compressionSummary(encoded: EncodedBody): Record<string, unknown> {
  return {
    sentCompression: encoded.compressed ? 'gzip' : 'plain',
    sentBodyBytes: bodySize(encoded.body),
    sentBodyPrefixHex: bodyPrefixHex(encoded.body),
    sentGzipMagic: hasGzipMagic(encoded.body),
    encodeAttempted: encoded.compression.attempted,
    compressionStreamAvailable: encoded.compression.compressionStreamAvailable,
    encodeOutcome: encoded.compression.outcome,
    encodeBodyBytes: encoded.compression.bodyBytes,
    encodeBodyPrefixHex: encoded.compression.bodyPrefixHex,
    encodeErrorName: encoded.compression.errorName,
    encodeErrorMessage: encoded.compression.errorMessage,
  };
}

function encodedBodyCapture(body: EncodedBody['body']): Record<string, unknown> {
  const bytes = bodyBytes(body);
  if (bytes.byteLength > MAX_DEBUG_ENCODED_BODY_BYTES) {
    return { captured: false, bytes: bytes.byteLength };
  }
  return { captured: true, bytes: bytes.byteLength, base64: bytesToBase64(bytes) };
}

function reportCollectFailure(
  base: string,
  publishableKey: string,
  debugReport: Record<string, unknown> | null,
  debug: boolean,
  failure: Record<string, unknown>,
): void {
  if (!debugReport) return;
  void sendDebugReport(base, publishableKey, { ...debugReport, failure }).catch((err) => {
    if (debug) {
      console.warn('[StaySignals] collect debug report failed', err);
    }
  });
}

async function sendDebugReport(
  base: string,
  publishableKey: string,
  report: Record<string, unknown>,
): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    await fetch(`${base}/v1/collect/debug`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'X-StaySignals-Key': publishableKey,
      },
      body: JSON.stringify(report),
      keepalive: false,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

function createDebugId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function bodyPrefixHex(body: EncodedBody['body']): string {
  return Array.from(bodyBytes(body).subarray(0, 4))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function hasGzipMagic(body: EncodedBody['body']): boolean {
  const bytes = bodyBytes(body);
  return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

function bodyBytes(body: EncodedBody['body']): Uint8Array {
  if (typeof body === 'string') return new TextEncoder().encode(body);
  return new Uint8Array(body);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function getErrorName(err: unknown): string {
  return err instanceof Error ? err.name : typeof err;
}

function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
